import { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import { employeesAPI } from '../services/api';
import { InlineLoader } from './loading';

interface EmployeeFullDetails {
  userId?: number;
  empCode?: string;
  empName?: string;
  firstName?: string;
  middleName?: string;
  lastName?: string;
  noticePeriod?: number;
  policy?: string;
  claimPolicy?: string;
  studentId?: string;
  companyName?: string;
  contactNo?: string;
  officialEmail?: string;
  personalEmail?: string;
  address?: string;
  dob?: string;
  gender?: string;
  fatherName?: string;
  aadhaarNo?: string;
  panNo?: string;
  bankAccount?: string;
  ifscCode?: string;
  uanNo?: string;
  pfNo?: string;
  esiNo?: string;
  [key: string]: any;
}

interface Props {
  isOpen: boolean;
  onClose: () => void;
  userId: number | string | null;
}

const Field = ({ label, value }: { label: string; value?: string | number | null }) => (
  <div className="flex flex-col gap-1">
    <span className="text-xs font-medium uppercase tracking-wide text-gray-500">{label}</span>
    <span className="text-sm text-gray-900">
      {value === undefined || value === null || value === '' ? '—' : value}
    </span>
  </div>
);

const SectionHeader = ({ children }: { children: React.ReactNode }) => (
  <h4 className="mt-6 mb-3 border-b border-gray-200 pb-2 text-sm font-semibold uppercase tracking-wider text-gray-700">
    {children}
  </h4>
);

export default function EmployeeDetailsModal({ isOpen, onClose, userId }: Props) {
  const [details, setDetails] = useState<EmployeeFullDetails | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || !userId) {
      setDetails(null);
      setError(null);
      return;
    }

    let cancelled = false;
    const load = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const res = await employeesAPI.getFullDetails(userId);
        if (!cancelled) {
          const payload = res.data?.data ?? res.data ?? null;
          setDetails(payload);
        }
      } catch (err: any) {
        if (!cancelled) {
          console.error('Failed to load employee full details:', err);
          setError('Failed to load employee details. Please try again.');
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [isOpen, userId]);

  if (!isOpen) return null;

  const fullName = details
    ? details.empName ||
      [details.firstName, details.middleName, details.lastName].filter(Boolean).join(' ') ||
      '—'
    : '';
  const titleText = details ? `${fullName}${details.empCode ? ` - ${details.empCode}` : ''}` : 'Employee Details';

  const bankDetail = details
    ? [details.bankAccount, details.ifscCode].filter(Boolean).join(' (') + (details.ifscCode ? ')' : '')
    : '';

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center p-4">
        <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm transition-opacity" onClick={onClose} />

        <div className="relative w-full max-w-5xl rounded-lg bg-white shadow-2xl">
          <div className="flex items-center justify-between rounded-t-lg bg-primary-600 px-6 py-4">
            <h3 className="text-lg font-semibold text-white">{titleText}</h3>
            <button
              onClick={onClose}
              className="rounded-full p-1 text-primary-100 transition hover:text-white"
              aria-label="Close modal"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          <div className="max-h-[70vh] overflow-y-auto px-6 py-4">
            {isLoading && (
              <div className="flex items-center justify-center py-16 text-gray-500">
                <InlineLoader label="Loading employee details…" />
              </div>
            )}

            {error && !isLoading && (
              <div className="rounded-md bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>
            )}

            {details && !isLoading && (
              <>
                <SectionHeader>Employment</SectionHeader>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-3">
                  <Field label="Notice Period (In Days)" value={details.noticePeriod} />
                  <Field label="Policy" value={details.policy} />
                  <Field label="Claim Policy" value={details.claimPolicy} />
                  <Field label="Student Id" value={details.studentId} />
                  <Field label="Company" value={details.companyName} />
                </div>

                <SectionHeader>Contact Details</SectionHeader>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-3">
                  <Field label="Mobile Number" value={details.contactNo} />
                  <Field label="Official Email Id" value={details.officialEmail} />
                  <Field label="Personal Email" value={details.personalEmail} />
                  <div className="sm:col-span-2 md:col-span-3">
                    <Field label="Full Address" value={details.address} />
                  </div>
                </div>

                <SectionHeader>Personal Information</SectionHeader>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-3">
                  <Field label="DOB" value={details.dob} />
                  <Field label="Gender" value={details.gender} />
                  <Field label="Father Name" value={details.fatherName} />
                  <Field label="Aadhaar Number" value={details.aadhaarNo} />
                  <Field label="PAN Number" value={details.panNo} />
                  <Field label="Bank Detail" value={bankDetail || undefined} />
                  <Field label="UAN Number" value={details.uanNo} />
                  <Field label="PF Number" value={details.pfNo} />
                  <Field label="ESI Number" value={details.esiNo} />
                </div>

                {/* The "Document Brief Case" section was REMOVED: identity and
                    employment documents (Aadhaar, PAN, cancelled cheque, appointment
                    letter, user photo) should not be viewable or downloadable by
                    everyone who can reach the Employees page.

                    ⚠️ UI-ONLY. `documents` still comes down in the
                    GET /employees/:userId/full-details response, so the list and its
                    S3 keys remain readable in the network tab.

                    ⚠️⚠️ DO NOT "fix" that by stripping `documents` from the controller.
                    That endpoint is SHARED with the mobile app: DocumentVaultScreen
                    reads `data.documents` from it (apps/mobile/.../DocumentVaultScreen.tsx),
                    which is an employee viewing their OWN documents — a legitimate
                    feature a blanket strip would break. Any server-side change has to
                    distinguish "caller is viewing someone else's record" (strip) from
                    "caller is viewing their own" (keep). */}
              </>
            )}
          </div>

          <div className="flex items-center justify-end rounded-b-lg border-t border-gray-200 bg-gray-50 px-6 py-4">
            <button
              onClick={onClose}
              className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 transition hover:bg-gray-100"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
